import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, FileSignature, Lock, PenTool } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function LandingPage() {
  const [numeroDocumento, setNumeroDocumento] = useState('');
  const [serial, setSerial] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatedConsent, setValidatedConsent] = useState(null);
  const [activeTab, setActiveTab] = useState('validar'); // 'validar' or 'firmar'
  const [codigoFirma, setCodigoFirma] = useState('');
  const [docFirma, setDocFirma] = useState('');
  const [loadingFirma, setLoadingFirma] = useState(false);
  const navigate = useNavigate();

  const handleValidate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/public/validate-consent`, {
        numero_documento: numeroDocumento,
        serial: serial
      });

      setValidatedConsent(response.data);
      toast.success('¡Documento válido!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Documento no encontrado');
      setValidatedConsent(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFirmar = async (e) => {
    e.preventDefault();
    setLoadingFirma(true);

    try {
      const response = await axios.post(`${API_URL}/public/validate-signing-code`, {
        codigo_firma: codigoFirma.toUpperCase(),
        numero_documento: docFirma
      });

      if (response.data.valid) {
        toast.success(`Bienvenido, ${response.data.patient_name}`);
        navigate(`/firmar/${response.data.link_token}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código de firma o documento no válido');
    } finally {
      setLoadingFirma(false);
    }
  };

  const handleDownload = async () => {
    // Open the tab synchronously (on the click itself) so popup blockers don't
    // treat it as an unrequested popup once the async request resolves; the
    // document number travels in the request body, never in the URL.
    const pdfWindow = window.open('', '_blank');
    try {
      const response = await axios.post(
        `${API_URL}/consents/download-pdf/${serial}`,
        { numero_documento: numeroDocumento },
        { responseType: 'blob' }
      );
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      if (pdfWindow) pdfWindow.location.href = blobUrl;
    } catch (error) {
      if (pdfWindow) pdfWindow.close();
      toast.error('Error al descargar el PDF');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="landing-page">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-violet-900 via-slate-950 to-black"></div>
        
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%239C92AC' fill-opacity='0.1' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E\")" }}></div>
        </div>

        <div className="relative z-10">
          <header className="container mx-auto px-4 py-6 flex justify-between items-center">
            <img src="/logo-konemed.png" alt="Konemed" className="h-10" data-testid="header-logo" />
            <button
              onClick={() => navigate('/login')}
              data-testid="login-button-header"
              className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg backdrop-blur-sm border border-white/20 transition-all"
            >
              Iniciar Sesión
            </button>
          </header>

          <div className="container mx-auto px-4 py-20 lg:py-32">
            <div className="text-center mb-16">
              <h2 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="hero-title">
                Validación de<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-fuchsia-400">
                  Consentimientos
                </span>
              </h2>
              <p className="text-xl text-violet-200 max-w-2xl mx-auto">
                Sistema seguro de gestión de consentimientos informados para clínicas
              </p>
            </div>

            <div className="max-w-xl mx-auto">
              <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl shadow-2xl overflow-hidden" data-testid="validation-card">
                {/* Tabs */}
                <div className="flex border-b border-white/20">
                  <button
                    onClick={() => { setActiveTab('validar'); setValidatedConsent(null); }}
                    data-testid="tab-validar"
                    className={`flex-1 py-4 text-center font-semibold transition-all ${
                      activeTab === 'validar' 
                        ? 'bg-white/10 text-white border-b-2 border-violet-400' 
                        : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <CheckCircle size={18} className="inline mr-2" />
                    Validar Consentimiento
                  </button>
                  <button
                    onClick={() => setActiveTab('firmar')}
                    data-testid="tab-firmar"
                    className={`flex-1 py-4 text-center font-semibold transition-all ${
                      activeTab === 'firmar' 
                        ? 'bg-white/10 text-white border-b-2 border-violet-400' 
                        : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <PenTool size={18} className="inline mr-2" />
                    Firmar
                  </button>
                </div>

                <div className="p-8">
                  {activeTab === 'validar' ? (
                    <>
                      <h3 className="text-2xl font-bold text-white mb-6 text-center">
                        Validar Consentimiento
                      </h3>
                      
                      <form onSubmit={handleValidate} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-violet-200 mb-2">
                            Número de Documento
                          </label>
                          <input
                            type="text"
                            value={numeroDocumento}
                            onChange={(e) => setNumeroDocumento(e.target.value)}
                            data-testid="input-documento"
                            className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                            placeholder="Ingrese su número de documento"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-violet-200 mb-2">
                            Serial del Consentimiento
                          </label>
                          <input
                            type="text"
                            value={serial}
                            onChange={(e) => setSerial(e.target.value.toUpperCase())}
                            data-testid="input-serial"
                            className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent font-mono"
                            placeholder="Ej: A1B2C3D4E5"
                            required
                          />
                        </div>

                        <button
                          type="submit"
                          disabled={loading}
                          data-testid="validate-button"
                          className="w-full py-3 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-violet-500/30"
                        >
                          {loading ? 'Validando...' : 'Validar Documento'}
                        </button>
                      </form>

                      {validatedConsent && (
                        <div className="mt-6 p-4 bg-emerald-500/20 border border-emerald-400/50 rounded-lg" data-testid="validation-result">
                          <div className="flex items-center gap-2 text-emerald-300 mb-2">
                            <CheckCircle size={20} />
                            <span className="font-semibold">{validatedConsent.message}</span>
                          </div>
                          <p className="text-sm text-emerald-200 mb-3">
                            Paciente: {validatedConsent.patient_name}
                          </p>
                          <button
                            onClick={handleDownload}
                            data-testid="download-button"
                            className="w-full py-2 bg-white text-violet-600 font-semibold rounded-lg hover:bg-violet-50 transition-colors"
                          >
                            Descargar Consentimiento
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <h3 className="text-2xl font-bold text-white mb-2 text-center">
                        Firmar Consentimiento
                      </h3>
                      <p className="text-violet-200 text-sm text-center mb-6">
                        Ingresa el código de firma proporcionado por tu centro médico
                      </p>
                      
                      <form onSubmit={handleFirmar} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-violet-200 mb-2">
                            Código de Firma
                          </label>
                          <input
                            type="text"
                            value={codigoFirma}
                            onChange={(e) => setCodigoFirma(e.target.value.toUpperCase())}
                            data-testid="input-codigo-firma"
                            className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent font-mono tracking-widest text-center text-lg"
                            placeholder="ABCD1234"
                            maxLength={8}
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-violet-200 mb-2">
                            Documento de Identificación
                          </label>
                          <input
                            type="text"
                            value={docFirma}
                            onChange={(e) => setDocFirma(e.target.value)}
                            data-testid="input-doc-firma"
                            className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                            placeholder="Número de documento del paciente"
                            required
                          />
                        </div>

                        <button
                          type="submit"
                          disabled={loadingFirma}
                          data-testid="firmar-button"
                          className="w-full py-3 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-violet-500/30"
                        >
                          {loadingFirma ? 'Verificando...' : 'Acceder a Firmar'}
                        </button>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white py-20">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold text-center text-slate-900 mb-12">
            ¿Por qué Konemed?
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center" data-testid="feature-security">
              <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="text-violet-600" size={32} />
              </div>
              <h4 className="text-xl font-bold text-slate-900 mb-2">Seguridad</h4>
              <p className="text-slate-600">
                Firma electrónica validada con códigos de verificación únicos
              </p>
            </div>

            <div className="text-center" data-testid="feature-speed">
              <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileSignature className="text-violet-600" size={32} />
              </div>
              <h4 className="text-xl font-bold text-slate-900 mb-2">Rapidez</h4>
              <p className="text-slate-600">
                Envío instantáneo por correo o WhatsApp con firma en minutos
              </p>
            </div>

            <div className="text-center" data-testid="feature-legal">
              <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock className="text-violet-600" size={32} />
              </div>
              <h4 className="text-xl font-bold text-slate-900 mb-2">Legalidad</h4>
              <p className="text-slate-600">
                Documentos con QR y serial único para validación oficial
              </p>
            </div>
          </div>
        </div>
      </div>

      <footer className="bg-slate-900 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-slate-400">
            © 2026 Konemed. Todos los derechos reservados.{' '}
            <a 
              href="/admin/login" 
              className="text-slate-500 hover:text-slate-300 transition-colors text-xs"
              data-testid="admin-link"
            >
              función
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
